import { Fragment } from 'react';

/****************
 * The small amount of markdown a model actually emits.
 *
 * Guillaume, 2026-09-04: "The chatbot use some ** to have some character in bold
 * but it does not work." Right — the answer was rendered as plain text, so the
 * model's emphasis arrived as literal asterisks. That is worse than no emphasis:
 * the reader sees punctuation where the model meant weight.
 *
 * ── Why not a markdown library ──────────────────────────────────────────────
 *
 * A full renderer brings a parser, a sanitiser and a decision about raw HTML,
 * for a surface whose entire vocabulary is bold, inline code and bullets. The
 * risk it adds is real — this text comes from a model, and a model can be talked
 * into emitting anything — while the feature it adds beyond these three is
 * nothing anyone asked for.
 *
 * So this handles exactly what appears, and everything it does not recognise
 * stays as text. No HTML is ever constructed from the model's output: the parts
 * become React elements, so there is no path from an answer to injected markup.
 ****************/

/** `**bold**` and `` `code` `` inside one line. */
function inline(text: string, keyPrefix: string) {
  // One pass over both, so `**a `b` c**` does not depend on which ran first.
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={key} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={key} className="px-1 py-0.5 rounded bg-white/[0.06] text-[0.9em]">
          {part.slice(1, -1)}
        </code>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

export function AnswerText({ text }: { text: string }) {
  const lines = text.split('\n');

  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (trimmed === '') return <div key={i} className="h-1" />;

        // A bullet keeps its shape rather than becoming a line starting with a
        // hyphen, which is how a list of four things reads as one paragraph.
        const bullet = trimmed.match(/^[-*•]\s+(.*)$/);
        if (bullet) {
          return (
            <div key={i} className="flex gap-2">
              <span className="text-muted-foreground shrink-0">•</span>
              <span className="min-w-0">{inline(bullet[1], String(i))}</span>
            </div>
          );
        }

        const numbered = trimmed.match(/^(\d+)[.)]\s+(.*)$/);
        if (numbered) {
          return (
            <div key={i} className="flex gap-2">
              <span className="text-muted-foreground shrink-0 tabular-nums">{numbered[1]}.</span>
              <span className="min-w-0">{inline(numbered[2], String(i))}</span>
            </div>
          );
        }

        // A heading gets weight, not a bigger font: these sit inside a chat
        // bubble and a 24px h2 in a 380px panel reads as shouting.
        const heading = trimmed.match(/^#{1,6}\s+(.*)$/);
        if (heading) {
          return <div key={i} className="font-semibold">{inline(heading[1], String(i))}</div>;
        }

        return <div key={i}>{inline(trimmed, String(i))}</div>;
      })}
    </div>
  );
}
